import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { auth } from './Auth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome } from '@fortawesome/free-solid-svg-icons';
import ConfirmationModal from '../Elements/ConfirmationModal';

interface TOTPResponse {
  secret: string;
  qrCode: string;
  backupCodes: string[];
  success: boolean;
  message: string;
}

interface EnableTOTPResponse {
  success: boolean;
  backupCodes: string[];
  message: string;
}

interface TOTPSetupProps {
  activeTabExit?: () => void;
}

const TOTPSetup: React.FC<TOTPSetupProps> = ({ activeTabExit }) => {
  const [step, setStep] = useState<number>(1);
  const [qrCode, setQrCode] = useState<string>('');
  const [secret, setSecret] = useState<string>('');
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [is2FAEnabled, setIs2FAEnabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalConfig, setModalConfig] = useState<{
    title: string;
    message: string;
    confirmText: string;
    type: 'danger' | 'warning' | 'info';
    onConfirm: () => void;
  }>({
    title: '',
    message: '',
    confirmText: '',
    type: 'info',
    onConfirm: () => { },
  });

  useEffect(() => {
    check2FAStatus();
  }, []);

  const check2FAStatus = async (): Promise<void> => {
    setLoading(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      const token = await currentUser.getIdToken();
      const response = await axios.get('/pool/api/totp/check', {
        params: { email: currentUser.email },
        headers: { Authorization: `Bearer ${token}` }
      });
      setIs2FAEnabled(response.data.requiresTOTP);
    } catch (err) {
      console.error('Check 2FA status error:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateTOTP = async (): Promise<void> => {
    setLoading(true);
    setError('');

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const response = await axios.post<TOTPResponse>(
        '/pool/api/totp/generate',
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      let qrCodeData = response.data.qrCode;
      if (qrCodeData) {
        if (qrCodeData.startsWith('http') || qrCodeData.startsWith('/')) {
          // If it's a URL, fetch it as a blob with the token to handle authentication
          try {
            const imgResponse = await axios.get(qrCodeData, {
              headers: { Authorization: `Bearer ${token}` },
              responseType: 'blob'
            });
            qrCodeData = URL.createObjectURL(imgResponse.data);
          } catch (imgErr) {
            console.error('Failed to fetch QR code image from URL:', imgErr);
            // Fallback to original URL if fetch fails
          }
        } else if (!qrCodeData.startsWith('data:') && !qrCodeData.startsWith('otpauth:')) {
          // If it's a raw base64 string, prepend the data URL prefix
          qrCodeData = `data:image/png;base64,${qrCodeData}`;
        }
      }

      setQrCode(qrCodeData);
      setSecret(response.data.secret);

      if (response.data.success === false) {
        setError(response.data.message || 'Failed to generate TOTP');
        return;
      }

      setStep(2);
    } catch (err: any) {
      console.error('Generate TOTP error:', err);
      setError(err.response?.data?.message || 'Failed to generate TOTP');
    } finally {
      setLoading(false);
    }
  };

  const enableTOTP = async (): Promise<void> => {
    if (verificationCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const response = await axios.post<EnableTOTPResponse>(
        '/pool/api/totp/enable',
        { token: verificationCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setBackupCodes(response.data.backupCodes || []);
        setIs2FAEnabled(true);
        setStep(3);
      } else {
        setError(response.data.message || 'Invalid verification code');
      }
    } catch (err: any) {
      console.error('Enable TOTP error:', err);
      setError(err.response?.data?.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const disableTOTP = async (): Promise<void> => {
    setLoading(true);
    setError('');

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('User not authenticated');

      const token = await currentUser.getIdToken();
      await axios.post('/pool/api/totp/disable', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setIs2FAEnabled(false);
      setStep(1);
      // alert('Two-Factor Authentication has been disabled.');
    } catch (err: any) {
      console.error('Disable TOTP error:', err);
      setError(err.response?.data?.message || 'Failed to disable TOTP');
    } finally {
      setLoading(false);
      setIsModalOpen(false);
    }
  };

  const resetSetupState = (): void => {
    setStep(1);
    setQrCode('');
    setSecret('');
    setVerificationCode('');
    setBackupCodes([]);
    setError('');
  };

  const handleDisableClick = () => {
    setModalConfig({
      title: 'Disable 2FA',
      message: 'Are you sure you want to disable Two-Factor Authentication? This will make your account less secure.',
      confirmText: 'Disable',
      type: 'danger',
      onConfirm: disableTOTP,
    });
    setIsModalOpen(true);
  };

  const resetTOTP = async (): Promise<void> => {
    setLoading(true);
    setError('');

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('User not authenticated');

      const token = await currentUser.getIdToken();
      // Disable first
      await axios.post('/pool/api/totp/disable', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setIs2FAEnabled(false);
      // Then start generate flow
      await generateTOTP();
    } catch (err: any) {
      console.error('Reset TOTP error:', err);
      setError(err.response?.data?.message || 'Failed to reset TOTP');
      setLoading(false);
    } finally {
      setIsModalOpen(false);
    }
  };

  const handleResetClick = () => {
    setModalConfig({
      title: 'Reset 2FA',
      message: 'Resetting 2FA will disable your current setup and start a new one. You must complete the new setup to keep 2FA enabled. Proceed?',
      confirmText: 'Reset',
      type: 'warning',
      onConfirm: resetTOTP,
    });
    setIsModalOpen(true);
  };

  const copyBackupCodes = (): void => {
    navigator.clipboard.writeText(backupCodes.join('\n'))
      .then(() => {
        alert('Backup codes copied to clipboard!');
      })
      .catch(() => {
        setError('Failed to copy codes');
      });
  };

  return (
    <div className="flex flex-col items-center py-4 md:py-8">
      <div className="w-full max-w-xl backdrop-blur-xl transition-all">
        <div className="mb-8 text-center px-2">
          <h2 className="text-xl md:text-2xl font-black text-[#0A3A6B] dark:text-blue-400 tracking-tight">Two-Factor Authentication</h2>
          <p className="text-slate-500 dark:text-slate-400 text-xs md:text-sm mt-1">Enhance your account security</p>
        </div>

        {step === 1 && (
          <div className="text-center animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="flex justify-center mb-8">
              <div className="p-6 bg-blue-50 dark:bg-blue-900/30 rounded-full shadow-inner ring-8 ring-blue-50/50 dark:ring-blue-900/20">
                <svg className="w-16 h-16 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
            </div>
            <p className="mb-8 text-slate-600 dark:text-slate-400 text-base md:text-lg leading-relaxed px-4">
              {is2FAEnabled
                ? 'Your account is currently protected with Two-Factor Authentication.'
                : 'Protect your account by adding an extra layer of security using Time-based One-Time Passwords (TOTP).'}
            </p>
            {is2FAEnabled ? (
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={handleResetClick}
                  disabled={loading}
                  className="flex-1 px-6 py-4 bg-amber-500 text-white rounded-2xl font-bold hover:bg-amber-600 disabled:opacity-50 transition-all shadow-lg shadow-amber-500/20 active:scale-95"
                >
                  {loading ? 'Processing...' : 'Reset 2FA'}
                </button>
                <button
                  onClick={handleDisableClick}
                  disabled={loading}
                  className="flex-1 px-6 py-4 bg-rose-500 text-white rounded-2xl font-bold hover:bg-rose-600 disabled:opacity-50 transition-all shadow-lg shadow-rose-500/20 active:scale-95"
                >
                  {loading ? 'Processing...' : 'Disable 2FA'}
                </button>
              </div>
            ) : (
              <button
                onClick={generateTOTP}
                disabled={loading}
                className="w-full sm:w-auto px-12 py-4 bg-gradient-to-r from-[#0A3A6B] to-[#0E4D92] text-white rounded-2xl font-bold hover:opacity-90 disabled:opacity-50 transition-all shadow-lg shadow-blue-500/20 active:scale-95"
              >
                {loading ? 'Generating...' : 'Get Started'}
              </button>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-300">
            <div className="p-4 bg-blue-50/50 dark:bg-blue-900/20 rounded-2xl border border-blue-100 dark:border-blue-800 transition-colors">
              <p className="text-blue-800 dark:text-blue-300 text-sm font-bold flex items-center gap-2">
                <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
                Scan QR Code with Authenticator App
              </p>
            </div>

            <div className="flex justify-center p-6 bg-white dark:bg-white rounded-3xl shadow-inner border border-slate-100 dark:border-transparent">
              {qrCode && (
                <img
                  src={qrCode}
                  alt="QR Code"
                  className="w-56 h-56 transition-transform hover:scale-105"
                />
              )}
            </div>

            <div>
              <p className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-3 uppercase tracking-[0.2em] ml-1">Manual Entry Key</p>
              <div className="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 flex items-center justify-between group transition-colors hover:border-blue-400 dark:hover:border-blue-500">
                <code className="text-[10px] md:text-sm font-mono break-all text-slate-800 dark:text-slate-200">{secret}</code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(secret);
                  }}
                  className="p-2.5 bg-white dark:bg-slate-800 text-slate-400 hover:text-blue-500 dark:hover:text-blue-400 rounded-xl transition-all shadow-sm active:scale-90"
                  title="Copy to clipboard"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="pt-2">
              <label className="block mb-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em] ml-1">Verification Code</label>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) =>
                  setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                className="w-full p-5 border border-slate-200 dark:border-slate-700 rounded-2xl text-center text-2xl md:text-4xl font-mono bg-white/50 dark:bg-slate-900/50 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all shadow-inner"
                placeholder="000000"
                maxLength={6}
              />
            </div>

            <button
              onClick={enableTOTP}
              disabled={loading || verificationCode.length !== 6}
              className="w-full py-4 bg-green-600 text-white rounded-2xl font-bold hover:bg-green-700 disabled:opacity-50 transition-all shadow-lg shadow-green-500/20 mt-4 active:scale-95"
            >
              {loading ? 'Verifying...' : 'Verify & Enable 2FA'}
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-8 text-center animate-in zoom-in-95 duration-400">
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-6 shadow-inner ring-8 ring-green-50/50 dark:ring-green-900/20">
                <svg className="w-12 h-12 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-2xl md:text-3xl font-black text-[#0A3A6B] dark:text-green-400 tracking-tight">2FA Secured!</h3>
              <p className="text-slate-500 dark:text-slate-400 text-xs md:text-base mt-2">Two-factor authentication is now active.</p>
            </div>

            <div className="bg-amber-50/50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800 rounded-[2rem] p-6 text-left shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/40 rounded-xl flex items-center justify-center text-amber-600 dark:text-amber-400 shrink-0">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold text-amber-800 dark:text-amber-400 text-lg tracking-tight">Save Backup Codes</h3>
                  <p className="text-amber-700/70 dark:text-amber-300/70 text-xs">Essential for account recovery</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-6">
                {backupCodes && backupCodes.length > 0 ? backupCodes.map((code, index) => (
                  <div
                    key={index}
                    className="bg-white/80 dark:bg-slate-900/80 p-3.5 rounded-2xl border border-amber-100 dark:border-amber-900/50 text-center font-mono text-xs md:text-sm font-bold text-slate-700 dark:text-slate-300 shadow-sm transition-all hover:scale-[1.02] hover:shadow-md cursor-default"
                  >
                    {code}
                  </div>
                )) : (
                  <div className="col-span-2 text-center text-amber-700 dark:text-amber-400 text-sm italic py-4">
                    No backup codes generated.
                  </div>
                )}
              </div>

              <button
                onClick={copyBackupCodes}
                className="w-full py-4 bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-300 rounded-2xl font-bold hover:bg-amber-200 dark:hover:bg-amber-800/80 transition-all border border-amber-200 dark:border-amber-700 flex items-center justify-center gap-2 active:scale-95 shadow-sm"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
                Copy All Codes
              </button>
            </div>

            <button
              onClick={resetSetupState}
              className="w-full py-4 bg-[#0A3A6B] text-white rounded-2xl font-bold hover:opacity-90 transition-all shadow-lg shadow-blue-500/20 active:scale-95"
            >
              Finish Setup
            </button>
          </div>
        )}

        {error && (
          <div className="mt-6 p-4 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-400 rounded-2xl text-sm font-bold flex items-center gap-3 animate-in fade-in zoom-in-95">
            <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={isModalOpen}
        title={modalConfig.title}
        message={modalConfig.message}
        confirmText={modalConfig.confirmText}
        type={modalConfig.type}
        onConfirm={modalConfig.onConfirm}
        onCancel={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default TOTPSetup;